import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProblemSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const problems = [
    {
      id: 1,
      title: "Unreliable Contractors",
      description: "Freelancers who disappear mid-project, miss deadlines, or deliver subpar work that requires expensive revisions.",
      icon: "AlertTriangle",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop",
      testimonial: {
        quote: "We hired three different freelancers for our website project. Two disappeared after taking payment, and the third delivered something completely unusable.",
        author: "Sarah Chen",
        role: "Marketing Director",
        company: "TechStart Inc."
      },
      stats: "73% of businesses report freelancer reliability issues"
    },
    {
      id: 2,
      title: "Scope Creep Nightmares",
      description: "Projects that balloon beyond original estimates with hidden costs, endless revisions, and unclear deliverables.",
      icon: "TrendingUp",
      image: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?w=600&h=400&fit=crop",
      testimonial: {
        quote: "What started as a $2,000 logo design ended up costing $8,000 with months of back-and-forth revisions. No clear scope was ever established.",
        author: "Michael Rodriguez",
        role: "Founder",
        company: "GreenTech Solutions"
      },
      stats: "68% of projects exceed initial budget estimates"
    },
    {
      id: 3,
      title: "Communication Breakdowns",
      description: "Delayed responses, language barriers, timezone conflicts, and unclear project updates that leave you in the dark.",
      icon: "MessageCircleX",
      image: "https://images.pixabay.com/photo/2016/11/29/06/15/adult-1867743_1280.jpg?w=600&h=400&fit=crop",
      testimonial: {
        quote: "Our freelancer would go silent for weeks, then send confusing updates in broken English. We never knew the real project status.",
        author: "Jennifer Walsh",
        role: "Operations Manager",
        company: "Retail Plus"
      },
      stats: "81% cite poor communication as top freelance issue"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % problems.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, problems.length]);

  const handleSlideChange = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % problems.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + problems.length) % problems.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-poppins text-gray-900 mb-6">
            Tired of Freelance
            <span className="text-red-600"> Horror Stories?</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            You're not alone. Most businesses struggle with these common freelance hiring challenges that cost time, money, and peace of mind.
          </p>
        </div>

        {/* Interactive Carousel */}
        <div className="relative bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="flex transition-transform duration-500 ease-in-out" 
               style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
            {problems.map((problem, index) => (
              <div key={problem.id} className="w-full flex-shrink-0">
                <div className="grid lg:grid-cols-2 gap-8 p-8 lg:p-12">
                  {/* Content Side */}
                  <div className="flex flex-col justify-center">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mr-4">
                        <Icon name={problem.icon} size={32} className="text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl lg:text-3xl font-bold font-poppins text-gray-900">
                          {problem.title}
                        </h3>
                        <div className="text-sm text-red-600 font-semibold mt-1">
                          {problem.stats}
                        </div>
                      </div>
                    </div>

                    <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                      {problem.description}
                    </p>

                    {/* Testimonial */}
                    <div className="bg-gray-50 rounded-2xl p-6">
                      <div className="flex items-start mb-4">
                        <Icon name="Quote" size={24} className="text-gray-400 mr-3 mt-1" />
                        <p className="text-gray-700 italic leading-relaxed">
                          "{problem.testimonial.quote}"
                        </p>
                      </div>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mr-4">
                          <Icon name="User" size={20} className="text-gray-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">
                            {problem.testimonial.author}
                          </div>
                          <div className="text-sm text-gray-600">
                            {problem.testimonial.role}, {problem.testimonial.company}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Image Side */}
                  <div className="flex items-center justify-center">
                    <div className="relative w-full max-w-md">
                      <div className="aspect-square rounded-2xl overflow-hidden shadow-lg">
                        <Image
                          src={problem.image}
                          alt={problem.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {/* Overlay Effect */}
                      <div className="absolute inset-0 bg-red-600 bg-opacity-10 rounded-2xl"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Controls */}
          <div className="absolute top-1/2 left-4 transform -translate-y-1/2">
            <button
              onClick={prevSlide}
              className="w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <Icon name="ChevronLeft" size={24} className="text-gray-600" />
            </button>
          </div>
          <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
            <button
              onClick={nextSlide}
              className="w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <Icon name="ChevronRight" size={24} className="text-gray-600" />
            </button>
          </div>

          {/* Slide Indicators */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
            {problems.map((_, index) => (
              <button
                key={index}
                onClick={() => handleSlideChange(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Auto-play Indicator */}
          <div className="absolute top-4 right-4">
            <div className={`w-2 h-2 rounded-full ${isAutoPlaying ? 'bg-green-500' : 'bg-gray-400'}`}></div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-lg text-gray-600 mb-6">
            Sound familiar? There's a better way to work with freelancers.
          </p>
          <div className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors cursor-pointer">
            <Icon name="ArrowDown" size={20} className="mr-2" />
            See Our Solution
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;